import { ComponentProps, ComponentType, Key, ReactElement, memo } from "react";

export interface ComponentWrapper {
  (inner: ReactElement): ReactElement;
}

export function composeWrappers(
  ...wrappers: ComponentWrapper[]
): ComponentWrapper {
  return (inner) =>
    wrappers.reduceRight((result, wrapper) => wrapper(result), inner);
}

export function Component({ children }: { children(): ReactElement }) {
  return children();
}

export function IterableMap<T>({
  children,
  keyFn,
  items,
}: {
  children(item: T, index: number): ReactElement;
  keyFn(item: T, index: number): Key;
  items: Iterable<T>;
}) {
  const result = [];
  for (const item of items) {
    const index = result.length;
    result.push(
      <Component key={keyFn(item, index)}>
        {() => children(item, index)}
      </Component>,
    );
  }
  return <>{result}</>;
}

export function IterableMapofMap<T, U>({
  children,
  keyFn,
  items,
  separator,
}: {
  children(item: T, index: number): ReactElement;
  keyFn(item: T, index: number): Key;
  items: Readonly<Array<Iterable<T>>>;
  separator?: ReactElement;
}) {
  const result = [];
  let index = 0;
  for (const group of items) {
    result.push(
      <>
        <IterableMap keyFn={keyFn} items={group}>
          {children}
        </IterableMap>
        {index != items.length && separator}
      </>,
    );
    index++;
  }
  return <>{result}</>;
}

export function genericMemo<T extends ComponentType<any>>(
  Component: T,
  propsAreEqual?: (
    prevProps: Readonly<ComponentProps<T>>,
    nextProps: Readonly<ComponentProps<T>>,
  ) => boolean,
): T {
  return memo(Component, propsAreEqual) as any;
}
